import { Info, Gem } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TooltipWrapper } from "components/permissions/permissions";
import { Attribute } from "../../models";
import { DataMapping } from "./choose-data";

interface AttributeTreeProps {
  dataMappings?: DataMapping[];
  selected: Attribute[];
  onChange: (checked: boolean, id: string, parentId?: string) => void;
}

export const AttributeTree = (props: AttributeTreeProps) => {

  const isSelected = (selected: Attribute[], id: string, parentId?: string) => {
    if (parentId) {
      const parent = selected.find((s) => s.key == parentId);
      if (parent) {
        //@ts-ignore
        return parent.attributes.indexOf(id) > -1;
      }
      return false;
    } else {
      const item = selected.find((s) => s.key == id);
      return !!item;
    }
  };

  const isParentSelected = (parentId: string) => {
    return isSelected(props.selected, parentId, undefined);
  };

  return (
    <div className="flex flex-col ml-6">
      {props.dataMappings?.map((dataMapping, idx: number) => (
        <div key={`data-tree-${idx}`}>
          <div className="flex items-center gap-1">
            <label
              className="flex items-center gap-2 flex-1 py-1 cursor-pointer"
              id={`label-${dataMapping.name.replaceAll(" ", "-")}`}
            >
              <Checkbox
                id={`checkbox-${dataMapping.name.replaceAll(" ", "-")}`}
                checked={isSelected(
                  props.selected,
                  dataMapping.id,
                  undefined
                )}
                onCheckedChange={(checked) =>
                  props.onChange(!!checked, dataMapping.id, undefined)
                }
              />
              <span className="text-sm">{dataMapping.name}</span>
            </label>
            {dataMapping.credits && (
              <Badge variant="outline" className="text-xs">
                {dataMapping.credits} Credits
              </Badge>
            )}
            {dataMapping.isPremium && (
              <TooltipWrapper
                style={{
                  width: "24px",
                  height: "24px",
                }}
                title="Premium Data"
              >
                <Gem color="#1876d3" />
              </TooltipWrapper>
            )}
          </div>
          <div className="flex flex-col ml-6">
            {dataMapping.children?.map((child) => (
              <div key={child.id} className="flex items-center gap-1">
                <label
                  className={`flex items-center gap-2 flex-1 py-1 ${
                    !isParentSelected(dataMapping.id) ? "opacity-50 pointer-events-none" : "cursor-pointer"
                  }`}
                  id={`label-${child.name.replaceAll(" ", "-")}`}
                >
                  <Checkbox
                    id={`checkbox-${child.name.replaceAll(" ", "-")}`}
                    checked={isSelected(
                      props.selected,
                      child.id,
                      dataMapping.id
                    )}
                    disabled={!isParentSelected(dataMapping.id)}
                    onCheckedChange={(checked) =>
                      props.onChange(!!checked, child.id, dataMapping.id)
                    }
                  />
                  <span className="text-sm">{child.name}</span>
                </label>
                {dataMapping.credits && (
                  <Badge
                    variant="outline"
                    className={`text-xs ${!isParentSelected(dataMapping.id) ? "opacity-50" : ""}`}
                  >
                    {child.credits} Credits
                  </Badge>
                )}
                {child.tooltip && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-4 text-primary cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="z-2000">
                      {child.tooltip}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
